/**
 * RunsheetList — the scrollable list of pickup stops.
 * Replaces <ion-list> + <ion-item ng-repeat> in templates/dashboard.html.
 *
 * One row layout serves both size classes: the order number, then the address with
 * the status Badge, the name, and the facts line (bags, payment type, balance, pickup
 * code). `runsheetRowFacts` decides what the facts line states. Each row is its own
 * card with a gap around it, so stops of the same status don't read as one block.
 *
 * A row's colours follow its status colour from `lineColour`, which the Run map's markers
 * share. The selected row (item.id == currentLine) takes the darker shades.
 */

import { ScrollArea, Box, Text, Badge, Group, Stack } from '@mantine/core';
import { IconPackage } from '@tabler/icons-react';

import { lineColour } from './lineColour';
import { runsheetRowFacts } from './runsheetRowFacts';
import type { LineColour } from './lineColour';
import type { RowBadge } from './runsheetRowFacts';
import type { RunsheetLine } from '@/types';

// The status colours that carry a Badge on the row.
const BADGES: Partial<Record<LineColour, RowBadge>> = {
  green: 'complete',
  orange: 'extra',
  violet: 'install',
};

function lineVariant(item: RunsheetLine): {
  base: LineColour;
  bg: string; // resting background
  color: string; // resting text/icon color
  activeBg: string;
  activeBorder: string;
  activeText: string;
  badge?: RowBadge;
} {
  const base = lineColour(item);
  return {
    base,
    bg: `${base}.1`,
    color: base === 'gray' ? 'gray.6' : `${base}.9`,
    activeBg: `${base}.3`,
    activeBorder: `var(--mantine-color-${base}-${base === 'gray' ? 7 : 8})`,
    activeText: `${base}.9`,
    badge: BADGES[base],
  };
}

// The space around each row card. Rows are tap targets, so it is the 8px minimum
// between adjacent targets.
const ROW_GAP = 8;

interface Props {
  lines: RunsheetLine[];
  currentLineId: number | null;
  onSelect: (item: RunsheetLine, index: number) => void;
}

export function RunsheetList({ lines, currentLineId, onSelect }: Props) {
  return (
    <ScrollArea h="100%" type="auto" bg="gray.0">
      <Stack gap={ROW_GAP} p={ROW_GAP}>
        {lines.map((item, index) => {
          const { base, bg, color, activeBg, activeBorder, activeText, badge } = lineVariant(item);
          const isCurrent = item.id === currentLineId;
          const statusColour = isCurrent ? activeText : color;
          const facts = runsheetRowFacts(item, badge);
          const edge = isCurrent ? activeBorder : `var(--mantine-color-${base}-3)`;

          return (
            <Box
              key={item.id}
              id={`item${item.id}`}
              onClick={() => onSelect(item, index)}
              px="xs"
              py={6}
              mih={56}
              style={{
                cursor: 'pointer',
                border: `1px solid ${edge}`,
                borderLeft: `6px solid ${edge}`,
                borderRadius: 'var(--mantine-radius-md)',
                boxShadow: isCurrent ? `inset 0 0 0 2px ${activeBorder}` : undefined,
                transition: 'all 0.15s ease',
              }}
              bg={isCurrent ? activeBg : bg}
            >
              <Group wrap="nowrap" gap="xs">
                <Text
                  fw={900}
                  c={statusColour}
                  w={32}
                  ta="center"
                  fz={isCurrent ? 'xl' : 'lg'}
                  style={{ flexShrink: 0 }}
                >
                  {item.displayorder}
                </Text>

                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text
                      fz="sm"
                      fw={600}
                      truncate
                      c={statusColour}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {item.deliveryaddress}
                    </Text>
                    {badge && (
                      // Mantine's `sm` Badge text is 10px, under the 12px text minimum.
                      <Badge
                        size="sm"
                        fz="xs"
                        variant="filled"
                        color={base}
                        style={{ flexShrink: 0 }}
                      >
                        {badge}
                      </Badge>
                    )}
                  </Group>
                  <Text fz="md" fw={700} truncate c={isCurrent ? activeText : undefined}>
                    {item.namewithid}
                  </Text>
                  {/* The facts wrap in a row too narrow for them, so none is cut off. */}
                  <Group gap={10} style={{ rowGap: 2 }}>
                    <Group gap={3} wrap="nowrap">
                      {/* Box sizes the icon, because it keeps a `size` prop for itself. */}
                      <Box
                        component={IconPackage}
                        w={16}
                        h={16}
                        c={statusColour}
                        role="img"
                        aria-label="Bags"
                      />
                      <Text fz="sm" fw={700}>
                        {item.num_bags}
                      </Text>
                    </Group>
                    <Text fz="sm" c="dimmed">
                      {facts.payment}
                    </Text>
                    {facts.balance && (
                      <Text fz="sm" fw={600} c={facts.balance.colour}>
                        {facts.balance.text}
                      </Text>
                    )}
                    {facts.pickupCode && (
                      <Text fz="sm" fw={600} c={statusColour}>
                        {facts.pickupCode}
                      </Text>
                    )}
                  </Group>
                </Stack>
              </Group>
            </Box>
          );
        })}
      </Stack>
    </ScrollArea>
  );
}
